import { Check, ChevronDown, X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';

interface Option {
  value: string;
  label: string;
  description?: string;
}

interface MultiDropdownProps {
  options: Option[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  label?: string;
  required?: boolean;
  error?: string;
  disabled?: boolean;
  maxSelections?: number;
  maxHeight?: string;
  className?: string;
}

const MultiDropdown: React.FC<MultiDropdownProps> = ({
  options,
  value = [],
  onChange,
  placeholder = 'Select options...',
  label,
  required = false,
  error,
  disabled = false,
  maxSelections,
  _maxHeight = '200px',
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const filteredOptions = options.filter(
    option =>
      option.label &&
      typeof option.label === 'string' &&
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedOptions = options.filter(option => value.includes(option.value));

  const handleToggleOption = (optionValue: string) => {
    if (value.includes(optionValue)) {
      onChange(value.filter(v => v !== optionValue));
    } else {
      if (maxSelections && value.length >= maxSelections) return;
      onChange([...value, optionValue]);
    }
  };

  const handleRemoveOption = (optionValue: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(value.filter(v => v !== optionValue));
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setIsOpen(false);
      setSearchTerm('');
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <div
        className={`relative min-h-[42px] p-2 border rounded-lg cursor-pointer bg-white transition-colors ${
          error
            ? 'border-red-300 focus-within:border-red-500 focus-within:ring-red-500'
            : 'border-gray-300 focus-within:border-red-500 focus-within:ring-red-500'
        } ${disabled ? 'bg-gray-50 cursor-not-allowed' : 'hover:border-gray-400'} ${
          isOpen ? 'ring-1 ring-red-500 border-red-500' : ''
        }`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <div className="flex flex-wrap gap-1 items-center min-h-[26px]">
          {selectedOptions.length === 0 ? (
            <span className="text-gray-500 text-sm">{placeholder}</span>
          ) : (
            selectedOptions.map(option => (
              <span
                key={option.value}
                className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full"
              >
                {option.label}
                {!disabled && (
                  <X
                    className="h-3 w-3 cursor-pointer hover:text-red-500"
                    onClick={e => handleRemoveOption(option.value, e)}
                  />
                )}
              </span>
            ))
          )}
        </div>

        <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
          <ChevronDown
            className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        </div>
      </div>

      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-hidden">
          <div className="p-2 border-b border-gray-200">
            <input
              type="text"
              placeholder="Search options..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500"
            />
          </div>

          <div className="max-h-48 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500">No options found</div>
            ) : (
              filteredOptions.map(option => {
                const isSelected = value.includes(option.value);
                const isDisabled = maxSelections && !isSelected && value.length >= maxSelections;

                return (
                  <div
                    key={option.value}
                    className={`px-3 py-2 text-sm cursor-pointer transition-colors ${
                      isDisabled ? 'text-gray-400 cursor-not-allowed' : 'hover:bg-gray-100'
                    } ${isSelected ? 'bg-red-50 text-red-600' : ''}`}
                    onClick={() => !isDisabled && handleToggleOption(option.value)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium">{option.label}</div>
                        {option.description && (
                          <div className="text-xs text-gray-500 mt-1">{option.description}</div>
                        )}
                      </div>
                      {isSelected && <Check className="h-4 w-4 text-red-600" />}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}

      {maxSelections && (
        <p className="mt-1 text-xs text-gray-500">
          {value.length}/{maxSelections} selected
        </p>
      )}
    </div>
  );
};

export default MultiDropdown;
